<template>
  <div class="page-container">
    <div class="page-header">
      <div>
        <h1 class="page-title">{{ storeName }}班表</h1>
        <p class="page-description">查看與管理此門市的所有設計師班表</p>
      </div>
      <div class="header-actions">
        <router-link :to="{ name: 'SalonSchedules' }" class="btn btn-secondary">
          <i class="ri-arrow-left-line"></i> 返回門市列表
        </router-link>
        <router-link
          :to="{
            name: 'SalonScheduleCreate',
            query: { storeId, returnToStore: '1' }
          }"
          class="btn btn-primary"
        >
          <i class="ri-add-line"></i> 新增排班
        </router-link>
      </div>
    </div>

    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>載入中...</p>
    </div>

    <template v-else>
      <div class="schedule-tabs" role="tablist" aria-label="班表狀態">
        <button
          v-for="tab in tabs"
          :key="tab.value"
          type="button"
          role="tab"
          :aria-selected="activeTab === tab.value"
          :class="{ active: activeTab === tab.value }"
          @click="activeTab = tab.value"
        >
          {{ tab.label }}
          <span class="tab-count">{{ lifecycleCount(tab.value) }}</span>
        </button>
      </div>

      <div class="table-wrapper">
        <table>
          <thead>
            <tr>
              <th>設計師</th>
              <th>類型</th>
              <th>排班月份／生效日</th>
              <th>失效日</th>
              <th>版本</th>
              <th>狀態</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            <template v-for="schedule in filteredSchedules" :key="schedule.id">
              <tr>
                <td>{{ schedule.stylistName }}</td>
                <td>{{ schedule.scheduleType === 'monthly' ? '月排班' : '週排班' }}</td>
                <td>{{ schedule.yearMonth || dateOnly(schedule.effectiveFrom) }}</td>
                <td>
                  {{ schedule.scheduleType === 'weekly'
                    ? (dateOnly(schedule.effectiveTo) || '永久')
                    : '-' }}
                </td>
                <td>v{{ schedule.versionNo }}</td>
                <td>
                  <span
                    class="schedule-status"
                    :class="`is-${scheduleLifecycleStatus(schedule)}`"
                  >
                    {{ scheduleLifecycleLabel(schedule) }}
                  </span>
                </td>
                <td class="action-buttons">
                  <router-link
                    :to="{
                      name: 'SalonScheduleEdit',
                      params: { id: schedule.id },
                      query: { returnToStore: '1' }
                    }"
                    class="btn btn-sm btn-primary"
                  >
                    <i class="ri-edit-line"></i> 編輯
                  </router-link>
                  <button class="btn btn-sm btn-secondary" @click="previewSchedule(schedule)">
                    預覽
                  </button>
                  <button
                    v-if="schedule.status === 'draft'"
                    class="btn btn-sm btn-success"
                    @click="publishSchedule(schedule)"
                  >
                    發布
                  </button>
                  <button
                    v-if="canDeleteSchedule(schedule)"
                    class="btn btn-sm btn-danger"
                    @click="deleteSchedule(schedule)"
                  >
                    <i class="ri-delete-bin-line"></i> 刪除
                  </button>
                </td>
              </tr>
              <tr v-if="upcomingReplacement(schedule)" class="schedule-transition-row">
                <td colspan="7">
                  <i class="ri-information-line"></i>
                  新週班表已發布；現行班表將於
                  {{ dateOnly(schedule.effectiveTo) }} 過期，新班表將於
                  {{ dateOnly(upcomingReplacement(schedule).effectiveFrom) }} 生效。
                </td>
              </tr>
            </template>
            <tr v-if="!filteredSchedules.length">
              <td colspan="7" class="empty-state">此門市目前沒有{{ activeTabLabel }}班表</td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>
  </div>
</template>

<script>
import { salonService } from '@/api/modules/salon.js';
import { storeService } from '@/api/modules/stores.js';
import { salonDateOnly } from '@/utils/salonDate.js';
import { showConfirm, showError, showSuccess } from '@/utils/swal.js';

export default {
  name: 'SalonStoreSchedules',
  data() {
    return {
      loading: false,
      storeName: '門市',
      schedules: [],
      activeTab: 'current',
      tabs: [
        { value: 'current', label: '現行' },
        { value: 'pending_effective', label: '待生效' },
        { value: 'expired', label: '已過期' },
        { value: 'draft', label: '草稿' }
      ]
    };
  },
  computed: {
    storeId() {
      return this.$route.params.storeId;
    },
    filteredSchedules() {
      return this.schedules.filter(schedule => this.scheduleTabStatus(schedule) === this.activeTab);
    },
    activeTabLabel() {
      return this.tabs.find(tab => tab.value === this.activeTab)?.label || '';
    }
  },
  mounted() {
    this.loadData();
  },
  methods: {
    async loadData() {
      this.loading = true;
      try {
        const [storeResponse, firstResponse] = await Promise.all([
          storeService.getStore(this.storeId),
          salonService.listSchedules({ page: 1, pageSize: 100, storeId: this.storeId })
        ]);
        this.storeName = storeResponse?.data?.storeName || '門市';
        const firstData = firstResponse?.data || {};
        const schedules = [...(firstData.items || [])];
        const totalPages = firstData.pagination?.totalPages || 1;
        if (totalPages > 1) {
          const remaining = await Promise.all(
            Array.from({ length: totalPages - 1 }, (_, index) =>
              salonService.listSchedules({
                page: index + 2,
                pageSize: 100,
                storeId: this.storeId
              })
            )
          );
          remaining.forEach(response => schedules.push(...(response?.data?.items || [])));
        }
        this.schedules = schedules;
      } catch (error) {
        await this.handleError(error, '門市班表載入失敗');
      } finally {
        this.loading = false;
      }
    },
    scheduleLifecycleStatus(schedule) {
      if (schedule.status !== 'published') return schedule.status;
      const today = salonDateOnly(new Date());
      if (this.dateOnly(schedule.supersededFrom)
        && this.dateOnly(schedule.supersededFrom) <= today) return 'expired';
      if (schedule.scheduleType === 'monthly') {
        const currentMonth = today.slice(0, 7);
        if (schedule.yearMonth > currentMonth) return 'pending_effective';
        if (schedule.yearMonth < currentMonth) return 'expired';
        return 'current';
      }
      const effectiveFrom = this.dateOnly(schedule.effectiveFrom);
      const effectiveTo = this.dateOnly(schedule.effectiveTo);
      if (effectiveFrom > today) return 'pending_effective';
      if (effectiveTo && effectiveTo < today) return 'expired';
      return 'current';
    },
    scheduleLifecycleLabel(schedule) {
      return {
        current: '現行',
        pending_effective: '待生效',
        expired: '已過期',
        draft: '草稿',
        archived: '已封存'
      }[this.scheduleLifecycleStatus(schedule)] || schedule.status;
    },
    lifecycleCount(status) {
      return this.schedules.filter(schedule => this.scheduleTabStatus(schedule) === status).length;
    },
    scheduleTabStatus(schedule) {
      const status = this.scheduleLifecycleStatus(schedule);
      return status === 'archived' ? 'expired' : status;
    },
    upcomingReplacement(schedule) {
      if (this.scheduleLifecycleStatus(schedule) !== 'current'
        || schedule.scheduleType !== 'weekly') return null;
      const today = salonDateOnly(new Date());
      return this.schedules
        .filter(candidate => (
          candidate.id !== schedule.id
          && candidate.stylistId === schedule.stylistId
          && candidate.scheduleType === 'weekly'
          && candidate.status === 'published'
          && this.dateOnly(candidate.effectiveFrom) > today
        ))
        .sort((a, b) => (
          this.dateOnly(a.effectiveFrom).localeCompare(this.dateOnly(b.effectiveFrom))
        ))[0] || null;
    },
    canDeleteSchedule(schedule) {
      return schedule.status === 'draft'
        || (schedule.scheduleType === 'weekly'
          && this.scheduleLifecycleStatus(schedule) === 'pending_effective');
    },
    async previewSchedule(schedule) {
      try {
        const response = await salonService.previewSchedule(schedule.id);
        const data = response.data;
        const count = data.reservationConflicts.length + data.crossStoreConflicts.length;
        if (count) {
          await showError('發現衝突', `共有 ${count} 筆衝突，處理完成前不可發布。`);
        } else {
          await showSuccess('檢查完成', '沒有發現衝突，可以發布。');
        }
      } catch (error) {
        await this.handleError(error, '衝突預覽失敗');
      }
    },
    async publishSchedule(schedule) {
      await this.runAction(() => salonService.publishSchedule(schedule.id), '班表已發布');
    },
    async deleteSchedule(schedule) {
      const range = schedule.yearMonth || this.dateOnly(schedule.effectiveFrom);
      const isPending = this.scheduleLifecycleStatus(schedule) === 'pending_effective';
      if (!await showConfirm(
        isPending ? '刪除待生效班表' : '刪除班表草稿',
        `確定刪除 ${schedule.stylistName} 的 ${range} ${isPending ? '待生效班表' : '班表草稿'}？此操作無法復原。`
      )) return;
      await this.runAction(
        () => salonService.deleteSchedule(schedule.id),
        isPending ? '待生效班表已刪除，前一版班表已接續' : '班表草稿已刪除'
      );
    },
    async runAction(action, message) {
      try {
        await action();
        await showSuccess('操作成功', message);
        await this.loadData();
      } catch (error) {
        await this.handleError(error, '操作失敗');
      }
    },
    dateOnly(value) {
      return salonDateOnly(value);
    },
    async handleError(error, fallback) {
      const message = error?.response?.data?.message || error?.message || fallback;
      await showError(fallback, message);
    }
  }
};
</script>

<style scoped>
@import '~@/assets/scss/components/_list-page-common.scss';

.page-description {
  color: #64748b;
  margin: 6px 0 0;
}

.header-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.schedule-tabs {
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  gap: 4px;
  margin-bottom: 20px;
  overflow-x: auto;
}

.schedule-tabs button {
  align-items: center;
  background: transparent;
  border: 0;
  border-bottom: 3px solid transparent;
  color: #64748b;
  cursor: pointer;
  display: inline-flex;
  font-weight: 600;
  gap: 7px;
  padding: 11px 16px;
  white-space: nowrap;
}

.schedule-tabs button:hover {
  color: #4338ca;
}

.schedule-tabs button.active {
  border-bottom-color: #4f46e5;
  color: #4338ca;
}

.tab-count {
  background: #e2e8f0;
  border-radius: 999px;
  color: #475569;
  font-size: 11px;
  min-width: 22px;
  padding: 2px 7px;
  text-align: center;
}

.schedule-tabs button.active .tab-count {
  background: #e0e7ff;
  color: #4338ca;
}

.table-wrapper table {
  min-width: 980px;
}

.action-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.schedule-status {
  border-radius: 999px;
  display: inline-flex;
  font-size: 12px;
  font-weight: 600;
  padding: 4px 9px;
  white-space: nowrap;
}

.schedule-status.is-current {
  background: #dcfce7;
  color: #166534;
}

.schedule-status.is-pending_effective {
  background: #fef3c7;
  color: #92400e;
}

.schedule-status.is-expired,
.schedule-status.is-archived {
  background: #e2e8f0;
  color: #475569;
}

.schedule-status.is-draft {
  background: #dbeafe;
  color: #1d4ed8;
}

.schedule-transition-row > td {
  background: #fffbeb;
  border-top: 0;
  color: #92400e;
  font-size: 13px;
  padding: 9px 12px;
}

.schedule-transition-row i {
  margin-right: 5px;
}

</style>
